import React, { useMemo } from 'react';
import { useExpanded, useFlexLayout, usePagination, useRowSelect, useSortBy, useTable } from 'react-table';
import { useSticky } from 'react-table-sticky';
import { getHiddenColumns } from '../table-helpers/get-hidden-columns';
import { getRowId } from '../table-helpers/get-row-id';
import { useAlerts } from '../table-helpers/use-alerts';
import { useDecorators } from '../table-helpers/use-decorators';
import { useExpandable } from '../table-helpers/use-expandable';
import { useSelection } from '../table-helpers/use-selection';
import { useHiddenColumnsSetter } from './use-hidden-columns-setter';
const DEFAULT_PAGE_SIZE = 10;
const selectHooks = (layoutType) => {
    const defaultHooks = [useSortBy, useExpanded, usePagination, useRowSelect];
    if (layoutType === 'block') {
        defaultHooks.push(useFlexLayout);
        defaultHooks.push(useSticky);
    }
    return defaultHooks;
};
/**
 * Custom hook for creating a table instance.
 *
 * This hook combines several features from the react-table library, such as
 * selectable rows, expandable rows, and custom hooks for different layout types.
 * It also sets hidden columns on the table instance.
 *
 * @param {object} tableOptions - The options for the table.
 * @param {boolean} selectable - Whether the rows in the table should be selectable.
 * @param {boolean} expandable - Whether the rows in the table should be expandable.
 * @param {string} layoutType - The layout type for the table.
 * @param {Array} columns - The columns for the table.
 * @returns {TableInstance} The table instance.
 */
/**
 * All possible Params:
 * @param {Array} columns - The columns for the table.
 * @param {object} initialState - The initial state for the table.
 * @param {Array} data - The data for the table.
 * @param {object} controlledState - The controlled state for the table.
 * @param {object} tableOptions - The options for the table.
 * @param {boolean} selectable - Whether the rows in the table should be selectable.
 * @param {boolean} expandable - Whether the rows in the table should be expandable.
 * @param {string} layoutType - The layout type for the table.
 * @returns {TableInstance} The table instance.
 * @example
 *
 */
export const useUiTable = (params) => {
    const { columns, initialState, data, controlledState, tableOptions, selectable = false, expandable = false, alertIcon = undefined, isRowExpandable, layoutType = 'table', skipHiddenColumnsSetter, decorators } = params;
    const initial = useMemo(() => ({
        pageSize: DEFAULT_PAGE_SIZE,
        hiddenColumns: getHiddenColumns(columns),
        ...initialState
    }), [initialState, columns]);
    const tableInstance = useTable({
        columns,
        data,
        getRowId,
        initialState: initial,
        useControlledState: (state) => {
            return React.useMemo(() => ({
                ...state,
                ...controlledState
            }), 
            // eslint-disable-next-line
            [state, controlledState]);
        },
        decorators,
        ...tableOptions
    }, ...selectHooks(layoutType), useAlerts(alertIcon), useExpandable(expandable, isRowExpandable), useSelection(selectable), 
    // must be the last hook to be able to decorate columns added by other hooks
    useDecorators);
    useHiddenColumnsSetter(columns, tableInstance, skipHiddenColumnsSetter);
    return tableInstance;
};
